import { Link } from "react-router-dom";
import CodeBlock from "../../common/code_block";
import ErrorCodesLink from "../../common/ErrorCodesLink";
import HeaderWithTag from "../../common/header_with_tag";
import { AccountEntity, IValidateAccountRequest } from "../../types/types";
import { HOST_API_URL } from "../../config";
import TryValidateAccount from "./try";

const requestCode: IValidateAccountRequest = {
  account: "201015982801",
  entity: AccountEntity.DBI,
  type: "bank",
};

const responseCode = `{
  "statusCode": 200,
  "message": "Success",
  "errorCode": null,
  "data": {
    "accountId": "201015982801",
    "name": "Abdirazak Ibrahim Abdirahman",
    "agentId": "",
    "subAgentId": null,
    "address": null,
    "dob": null,
    "msisdn": null,
    "currency": "USD",
    "accountType": "BANK",
    "operationMode": "DBI",
    "mandates": null
  }
}`;

const failureResponseCode = `{
  "statusCode": 400,
  "message": "account validation failed",
  "errorCode": "DPI-2003",
  "data": null
}`;

function ValidateAccount() {
  return (
    <div className="page">
      <div className="app-content main-content mt-0">
        <div className="side-app">
          <div className="main-container container-fluid">
            <div className="page-header">
              <div>
                <h1 className="page-title">Validate Account</h1>
              </div>
              <div className="ms-auto pageheader-btn">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="javascript:void(0);">Docs</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Validate Account
                  </li>
                </ol>
              </div>
            </div>

            <div className="card overflow-hidden">
              <div className="row">
                <div className="col-md-7" style={{ position: "relative" }}>
                  <div className="card-body border-end p-0">
                    <div className="p-5">
                      <HeaderWithTag
                        headerText="Validate an account"
                        headerTag="description"
                      />
                      <p className="text-12">
                        <span className="badge badge-sm bg-blue text-white me-2">
                          POST
                        </span>
                        {HOST_API_URL}/Remittances/ValidateAccount
                      </p>
                      <p className="text-muted text-14" style={{ textAlign: "justify" }}>
                        Confirms whether an account is valid and eligible for a remittance. Run this check before initiating related transactions.
                      </p>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Request"
                          headerTag="request"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameter</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                                <th>Min</th>
                                <th>Max</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Account</td>
                                <td>string</td>
                                <td>Account number</td>
                                <td>Yes</td>
                                <td>5</td>
                                <td>30</td>
                              </tr>
                              <tr>
                                <td>Entity</td>
                                <td>string</td>
                                <td>
                                  Entity name, spelled exactly as listed in the Appendix
                                  (EDAHAB, MPESA, DBI, PESALink, …). Which entities are
                                  valid depends on Type.
                                </td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>Type</td>
                                <td>string</td>
                                <td>
                                  wallet or bank (case-insensitive). Any other value
                                  returns DPI-2001.
                                </td>
                                <td>Yes</td>
                                <td></td>
                                <td></td>
                              </tr>
                              <tr>
                                <td>Code</td>
                                <td>string</td>
                                <td>
                                  Destination bank or network code. Required for PESALink
                                  and ABC lookups.
                                </td>
                                <td>No</td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={JSON.stringify(requestCode, null, 2)}
                          codeBlackText="Request"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <HeaderWithTag
                          headerText="Response"
                          headerTag="response"
                        />
                        <div className="table-responsive">
                          <table className="table text-nowrap text-md-nowrap table-bordered">
                            <thead>
                              <tr>
                                <th>Parameters</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>statusCode</td>
                                <td>integer</td>
                                <td>200 on success, otherwise the failure status</td>
                              </tr>
                              <tr>
                                <td>message</td>
                                <td>string</td>
                                <td>"Success", or the reason for the failure</td>
                              </tr>
                              <tr>
                                <td>errorCode</td>
                                <td>string</td>
                                <td>null on success; a stable DPI-xxxx code on failure</td>
                              </tr>
                              <tr>
                                <td>data.accountId</td>
                                <td>string</td>
                                <td>The validated account or wallet number</td>
                              </tr>
                              <tr>
                                <td>data.name</td>
                                <td>string</td>
                                <td>Account holder's name</td>
                              </tr>
                              <tr>
                                <td>data.agentId</td>
                                <td>string</td>
                                <td>Agent identifier for the destination</td>
                              </tr>
                              <tr>
                                <td>data.subAgentId</td>
                                <td>string</td>
                                <td>Sub-agent identifier, where applicable</td>
                              </tr>
                              <tr>
                                <td>data.address</td>
                                <td>string</td>
                                <td>Account holder's address, where available</td>
                              </tr>
                              <tr>
                                <td>data.dob</td>
                                <td>string</td>
                                <td>Account holder's date of birth, where available</td>
                              </tr>
                              <tr>
                                <td>data.msisdn</td>
                                <td>string</td>
                                <td>Wallet MSISDN, for wallet lookups</td>
                              </tr>
                              <tr>
                                <td>data.currency</td>
                                <td>string</td>
                                <td>Currency of the validated account</td>
                              </tr>
                              <tr>
                                <td>data.accountType</td>
                                <td>string</td>
                                <td>Echoes the requested Type, upper-cased</td>
                              </tr>
                              <tr>
                                <td>data.operationMode</td>
                                <td>string</td>
                                <td>Echoes the requested Entity</td>
                              </tr>
                              <tr>
                                <td>data.mandates</td>
                                <td>object</td>
                                <td>Additional account mandates, where applicable</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={responseCode}
                          codeBlackText="Response Simple"
                          codeLanguage="json"
                        />
                      </div>

                      <div className="my-5">
                        <CodeBlock
                          code={failureResponseCode}
                          codeBlackText="Failure Example"
                          codeLanguage="json"
                        />
                      </div>

                      <ErrorCodesLink />
                    </div>
                  </div>
                </div>
                <div className="col-md-5 pt-5">
                  <h5 className="card-title text-primary">
                    Code Samples
                  </h5>

                  <TryValidateAccount
                    codeBlackTitle="Request Simple"
                    validateAccountObject={requestCode}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ValidateAccount;